import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
    type ChargeResponse,
    type DisableAdOrder,
    type Order,
    type PaymentModalOrderResponse,
    type Topup,
} from '@/modules/in-game-payments'
import { normalizeOrientation } from '@/utils/screen-orientation'

const emptyStatus = {
    order: null as Order | DisableAdOrder | Topup | null,
    resolve: null as (<T extends Order | DisableAdOrder | Topup>(value: ChargeResponse<T>) => void) | null,
    reject: null as ((reason: unknown) => void) | null,
}

export const useLayoutStore = defineStore('layout', () => {
    const fullScreen = ref(false)

    function setFullscreen(value: boolean) {
        fullScreen.value = value
    }

    const inviteModalOpenedReason = ref<boolean | 'need_more_fennecs'>(false)

    function setInviteModalOpened(value: typeof inviteModalOpenedReason.value) {
        inviteModalOpenedReason.value = value
    }

    const signInModalOpened = ref(false)
    const signInModalOpenedSource = ref<string>()

    function openSignInModal(source: string) {
        signInModalOpened.value = true
        signInModalOpenedSource.value = source
    }

    function closeSignInModal() {
        signInModalOpened.value = false
        signInModalOpenedSource.value = undefined
    }

    const installGooglePlayModalOpened = ref(false)

    function openInstallGooglePlayModal() {
        installGooglePlayModalOpened.value = true
    }

    function closeInstallGooglePlayModal() {
        installGooglePlayModalOpened.value = false
    }

    const playOnAppModalOpened = ref(false)

    function openPlayOnAppModal() {
        playOnAppModalOpened.value = true
    }

    function closePlayOnAppModal() {
        playOnAppModalOpened.value = false
    }

    const isReportBugModalOpen = ref(false)

    function setIsReportBugModalOpen(value: boolean) {
        isReportBugModalOpen.value = value
    }

    const paymentModalOrder = ref(emptyStatus)
    const isPaymentModalOpen = computed(() => Boolean(paymentModalOrder.value.order))

    function setPaymentModalOrder<T = Order | DisableAdOrder | Topup | null>(
        value: Order | DisableAdOrder | Topup | null,
    ) {
        if (!value) {
            paymentModalOrder.value.order = null
            paymentModalOrder.value.reject = null
            paymentModalOrder.value.resolve = null
            return undefined as PaymentModalOrderResponse<T>
        }

        return new Promise<ChargeResponse<typeof value>>((resolve, reject) => {
            paymentModalOrder.value.order = value
            paymentModalOrder.value.resolve = resolve
            paymentModalOrder.value.reject = reject
        }) as PaymentModalOrderResponse<T>
    }

    const isTabFocused = ref(true)

    function setTabFocused(value: boolean) {
        isTabFocused.value = value
    }

    const screenOrientation = ref<string>()

    function setOrientation(type: string) {
        screenOrientation.value = normalizeOrientation(type)
    }

    return {
        setFullscreen,
        fullScreen,
        setInviteModalOpened,
        inviteModalOpenedReason,
        openSignInModal,
        closeSignInModal,
        signInModalOpened,
        signInModalOpenedSource,
        openInstallGooglePlayModal,
        closeInstallGooglePlayModal,
        installGooglePlayModalOpened,
        openPlayOnAppModal,
        closePlayOnAppModal,
        playOnAppModalOpened,
        setIsReportBugModalOpen,
        isReportBugModalOpen,
        isPaymentModalOpen,
        paymentModalOrder,
        setPaymentModalOrder,
        isTabFocused,
        setTabFocused,
        screenOrientation,
        setOrientation,
    }
})
